import type { PartialBy } from '@iconik-io/core/src/types';
import {
  type FilesEditProxySettingsSchema,
  type FilesEditReadyTranscoderSchema,
  type FilesElementalMediaConvertSchema,
  type FilesElementalServerSchema,
  type FilesFFmpegSettingsSchema,
  type FilesFlicsTranscoderSchema,
  type FilesHttpGetTranscodersQuery,
  type FilesIconikEdgeTranscoderSchema,
  type FilesImageMagickSettingsSchema,
  type FilesRedlineSchema,
  type FilesTranscoderSchema,
  type FilesVantageSettingsSchema,
  type FilesWildmokaSettingsSchema,
  httpGetTranscoders,
  httpPatchTranscodersByTranscoderId,
  httpPostTranscoders,
} from '@iconik-io/iconik-api/src/services/files';

type CommonSettings = {
  local?: boolean;
  activate?: boolean;
};

/**
 * Due to some API library limitations some fields are not possible to type properly
 * in schemas directly on the API side.
 */
type FilesTranscoderSchemaOmit = Omit<
  FilesTranscoderSchema,
  'id' | 'settings' | 'type'
>;
export type FilesTranscoderSchemaPatched =
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesEditReadyTranscoderSchema;
      type: 'EDITREADY';
      id: string;
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings & FilesFFmpegSettingsSchema;
      type: 'FFMPEG';
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings & FilesEditProxySettingsSchema;
      type: 'EDIT_PROXY';
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings & FilesVantageSettingsSchema;
      type: 'VANTAGE';
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings & FilesImageMagickSettingsSchema;
      type: 'IMAGEMAGICK';
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings &
        PartialBy<FilesElementalMediaConvertSchema, 'secret_key'>;
      type: 'ELEMENTAL_MEDIACONVERT';
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings &
        PartialBy<FilesElementalServerSchema, 'api_key'>;
      type: 'ELEMENTAL_SERVER';
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings & FilesRedlineSchema;
      type: 'REDLINE';
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings & FilesIconikEdgeTranscoderSchema;
      type: 'ICONIK_EDGE_TRANSCODER';
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings & FilesFlicsTranscoderSchema;
      type: 'FLICS';
    })
  | (FilesTranscoderSchemaOmit & {
      id: string;
      settings: CommonSettings &
        PartialBy<FilesWildmokaSettingsSchema, 'wm_secret_key'>;
      type: 'WILDMOKA';
    });

export type FilesTranscoderSchemaCreatePatched =
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesEditReadyTranscoderSchema;
      type: 'EDITREADY';
    })
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesFFmpegSettingsSchema;
      type: 'FFMPEG';
    })
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesEditProxySettingsSchema;
      type: 'EDIT_PROXY';
    })
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesVantageSettingsSchema;
      type: 'VANTAGE';
    })
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesImageMagickSettingsSchema;
      type: 'IMAGEMAGICK';
    })
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesElementalMediaConvertSchema;
      type: 'ELEMENTAL_MEDIACONVERT';
    })
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesElementalServerSchema;
      type: 'ELEMENTAL_SERVER';
    })
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesRedlineSchema;
      type: 'REDLINE';
    })
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesIconikEdgeTranscoderSchema;
      type: 'ICONIK_EDGE_TRANSCODER';
    })
  | (FilesTranscoderSchemaOmit & {
      settings: CommonSettings & FilesWildmokaSettingsSchema;
      type: 'WILDMOKA';
    });

/**
 * The transcoders endpoint filters on the transcoder's locality and its type,
 * but neither reaches the generated query schema.
 */
/**
 * `type` is generated as a single string, but the admin filter panel sends the
 * whole facet selection and the client serializes arrays with
 * `arrayFormat: 'repeat'`.
 */
export type FilesHttpGetTranscodersQueryPatched = Omit<
  FilesHttpGetTranscodersQuery,
  'type'
> & {
  'settings.local'?: string;
  type?: string | string[];
};

/**
 * The generated schemas type the settings as one shared bag, while the patched
 * shapes above pair each `type` with the settings it actually carries. The
 * widenings back to the generated request bodies are stated once here.
 */
export const getTranscodersRequest = (
  query: FilesHttpGetTranscodersQueryPatched,
) => httpGetTranscoders(query);

export const createTranscoderRequest = (
  transcoder: FilesTranscoderSchemaCreatePatched,
) =>
  // biome-ignore lint/plugin: see getTranscodersRequest.
  httpPostTranscoders(transcoder as FilesTranscoderSchema);

export const updateTranscoderRequest = (
  transcoder: FilesTranscoderSchemaPatched,
) =>
  httpPatchTranscodersByTranscoderId(
    transcoder.id,
    // biome-ignore lint/plugin: see getTranscodersRequest.
    transcoder as FilesTranscoderSchema,
  );

/** The response carries the same paired settings, back the other way. */
export const asTranscoder = (
  body: FilesTranscoderSchema,
): FilesTranscoderSchemaPatched =>
  // biome-ignore lint/plugin: see getTranscodersRequest.
  body as FilesTranscoderSchemaPatched;
